/**
 * Biomarker Configuration — shared across web and mobile.
 *
 * Contains:
 * - FUNCTIONAL_SYSTEMS: category groupings
 * - PROVIDER_PRIORITY: device deduplication order
 * - BIOMARKER_CONFIG: all wearable + lab biomarker definitions keyed by LOINC code
 * - Helper functions for querying the config
 *
 * Web-only Set-based helpers (CONTINUOUS_CATEGORY_CODES, CONTINUOUS_LOINC_CODES,
 * EXCLUDED_FROM_DISCRETE_LOINC_CODES) remain in apps/web/lib/trends/config.ts
 * because Set is not JSON-serialisable.
 *
 * NOTE ON FILE SIZE: this file is intentionally large. It is a curated tabular
 * constant (LOINC → BiomarkerConfig) plus its reference-range table; splitting it
 * by panel would fragment cross-cutting lookups (getBiomarkerConfig, panel/category
 * queries) that must scan the whole set. Keep it as one authoritative table.
 *
 * @see docs/trends/architecture.md for full architecture documentation
 */

import { VitalLOINCCodes } from "../wearables/loinc";

import { CLINIC_PROVIDER_SLUG } from "./constants";
import type { BiomarkerConfig, FunctionalSystem, LabPanel, ReferenceRange } from "./types";

// -----------------------------------------------------------------------------
// Lab Panel Ordering
// -----------------------------------------------------------------------------

/**
 * Display order for lab test / panel groups in the "By Test / Panel" sort mode.
 * Biomarkers without a `panel` (or not in the curated config) sort after all
 * of these as an implicit "Other" group.
 */
export const LAB_PANEL_ORDER: readonly LabPanel[] = [
  // Routine panels first, ordered as a clinician scans a chemistry + CBC workup.
  "lipid",
  "cbc",
  "bmp",
  "renal",
  "liver",
  "thyroid",
  "glycemic",
  "iron",
  "vitamins",
  "inflammation",
  "hormones",
  // Specialty panels follow the routine set.
  "cardiac",
  "bone",
  "muscle",
  "pancreatic",
  "autoimmune",
  "infectious",
  "toxicology",
] as const;

/** Human-readable labels for lab panels, used for the table panel subtext. */
export const LAB_PANEL_LABELS: Record<LabPanel, string> = {
  lipid: "Lipid Panel",
  cbc: "Complete Blood Count",
  bmp: "Basic Metabolic Panel",
  renal: "Renal Function",
  liver: "Liver Panel",
  thyroid: "Thyroid Panel",
  glycemic: "Glycemic",
  iron: "Iron Studies",
  vitamins: "Vitamins & Minerals",
  inflammation: "Inflammation",
  hormones: "Hormones",
  cardiac: "Cardiac Markers",
  bone: "Bone Health",
  muscle: "Muscle Health",
  pancreatic: "Pancreatic",
  autoimmune: "Autoimmune",
  infectious: "Infectious Disease",
  toxicology: "Toxicology",
};

// CLINIC_PROVIDER_SLUG is exported by constants.ts — no re-export needed here.

// -----------------------------------------------------------------------------
// Provider Priority
// -----------------------------------------------------------------------------

/**
 * Priority order for device providers when deduplicating data.
 * Higher priority providers are preferred when data exists from multiple sources.
 */
export const PROVIDER_PRIORITY: readonly string[] = [
  CLINIC_PROVIDER_SLUG,
  "oura",
  "whoop",
  "garmin",
  "apple_health_kit",
  "fitbit",
  "withings",
  "polar",
  "samsung_health",
  "manual",
] as const;

// -----------------------------------------------------------------------------
// Functional Systems
// -----------------------------------------------------------------------------

/**
 * Functional system definitions for grouping biomarkers.
 * The first 9 match the Figma design; "other" is a hidden bucket for
 * dynamically discovered lab biomarkers not in the curated config.
 *
 * Note: "other" is intentionally excluded from TREND_CATEGORIES (constants.ts)
 * so it doesn't appear in the UI category picker.
 *
 * @see https://www.figma.com/design/pqY81U2nOTtJeHfVVHpk2Q/EHR-Trends-Exploration?node-id=2650-3328
 */
export const FUNCTIONAL_SYSTEMS: Record<
  FunctionalSystem,
  { name: string; description: string; icon: string }
> = {
  metabolism: {
    name: "Metabolism",
    description: "Glucose, lipids, activity, body composition",
    icon: "flame",
  },
  cardiovascular: {
    name: "Cardiovascular",
    description: "Heart rate, HRV, blood oxygen",
    icon: "heart",
  },
  autonomic: {
    name: "Autonomic",
    description: "Nervous system, stress recovery, HRV",
    icon: "activity-heartbeat",
  },
  inflammation: {
    name: "Inflammation",
    description: "CRP, white blood cells, inflammatory markers",
    icon: "shield",
  },
  endocrine: {
    name: "Endocrine",
    description: "Thyroid, sex & adrenal hormones",
    icon: "flask",
  },
  muscular_skeletal: {
    name: "Musculoskeletal",
    description: "Strength, mobility, musculoskeletal health",
    icon: "bone",
  },
  sleep: {
    name: "Sleep",
    description: "Sleep duration, efficiency, quality",
    icon: "zzz",
  },
  nutrients: {
    name: "Nutrients",
    description: "Vitamins, minerals, blood markers",
    icon: "leaf",
  },
  digestive: {
    name: "Digestive",
    description: "Liver enzymes, GI health",
    icon: "apple",
  },
  other: {
    name: "Other",
    description: "Uncategorized biomarkers from lab uploads",
    icon: "dots",
  },
} as const;

// -----------------------------------------------------------------------------
// Reference Ranges
// -----------------------------------------------------------------------------

/**
 * Standard reference ranges for wearable metrics.
 * These are general population ranges; individual goals may differ.
 */
const WEARABLE_RANGES: Record<string, ReferenceRange> = {
  hrv: {
    normal: { min: 20, max: 100 },
    optimal: { min: 40, max: 80 },
  },
  restingHeartRate: {
    normal: { min: 40, max: 100 },
    optimal: { min: 50, max: 70 },
  },
  bloodOxygen: {
    normal: { min: 90, max: 100 },
    optimal: { min: 95, max: 100 },
  },
  sleepDuration: {
    normal: { min: 360, max: 600 }, // 6-10 hours in minutes
    optimal: { min: 420, max: 540 }, // 7-9 hours
  },
  sleepEfficiency: {
    normal: { min: 75, max: 100 },
    optimal: { min: 85, max: 100 },
  },
  steps: {
    normal: { min: 5000, max: 25000 },
    optimal: { min: 8000, max: 15000 },
  },
  bodyWeight: {
    normal: { min: 40, max: 150 },
    optimal: { min: 50, max: 100 }, // Very general; should use BMI
  },
  glucose: {
    normal: { min: 70, max: 140 },
    optimal: { min: 70, max: 100 },
  },
  // Blood Pressure
  bloodPressureSystolic: {
    normal: { min: 90, max: 140 },
    optimal: { min: 110, max: 130 },
  },
  bloodPressureDiastolic: {
    normal: { min: 60, max: 90 },
    optimal: { min: 65, max: 85 },
  },
  pulsePressure: {
    normal: { min: 25, max: 60 },
    optimal: { min: 30, max: 50 },
  },
  nighttimeHR: {
    normal: { min: 40, max: 70 },
    optimal: { min: 45, max: 60 },
  },
  // Temperature
  bodyTemperature: {
    normal: { min: 36.1, max: 37.2 },
    optimal: { min: 36.4, max: 37.0 },
  },
  // CGM Computed
  cgmTimeInRange: {
    normal: { min: 50, max: 100 },
    optimal: { min: 70, max: 100 },
  },
  cgmVariability: {
    normal: { min: 0, max: 36 },
    optimal: { min: 0, max: 33 },
  },
  // Sleep
  sleepLatency: {
    normal: { min: 0, max: 45 },
    optimal: { min: 5, max: 20 },
  },
  waso: {
    normal: { min: 0, max: 60 },
    optimal: { min: 0, max: 20 },
  },
  sleepRegularity: {
    normal: { min: 50, max: 100 },
    optimal: { min: 75, max: 100 },
  },
  // Activity
  activeMinutes: {
    normal: { min: 15, max: 180 },
    optimal: { min: 30, max: 60 },
  },
};

/**
 * Standard reference ranges for lab results.
 * These are based on typical clinical reference ranges.
 */
const LAB_RANGES: Record<string, ReferenceRange> = {
  totalCholesterol: {
    normal: { min: 0, max: 240 },
    optimal: { min: 0, max: 200 },
  },
  hdlCholesterol: {
    normal: { min: 40, max: 100 },
    optimal: { min: 60, max: 100 },
  },
  ldlCholesterol: {
    normal: { min: 0, max: 160 },
    optimal: { min: 0, max: 100 },
  },
  triglycerides: {
    normal: { min: 0, max: 200 },
    optimal: { min: 0, max: 150 },
  },
  wbc: {
    normal: { min: 4.5, max: 11.0 },
    optimal: { min: 5.0, max: 10.0 },
  },
  rbc: {
    normal: { min: 4.0, max: 6.0 },
    optimal: { min: 4.5, max: 5.5 },
  },
  hemoglobin: {
    normal: { min: 12.0, max: 17.5 },
    optimal: { min: 13.0, max: 16.0 },
  },
  alt: {
    normal: { min: 0, max: 56 },
    optimal: { min: 0, max: 40 },
  },
  ast: {
    normal: { min: 0, max: 40 },
    optimal: { min: 0, max: 35 },
  },
  tsh: {
    normal: { min: 0.4, max: 4.0 },
    optimal: { min: 1.0, max: 2.5 },
  },
  vitaminD: {
    normal: { min: 20, max: 100 },
    optimal: { min: 40, max: 80 },
  },
  fastingGlucose: {
    normal: { min: 70, max: 100 },
    optimal: { min: 70, max: 90 },
  },
  hba1c: {
    normal: { min: 4.0, max: 5.7 },
    optimal: { min: 4.5, max: 5.4 },
  },
  crp: {
    normal: { min: 0, max: 3.0 },
    optimal: { min: 0, max: 1.0 },
  },
  esr: {
    normal: { min: 0, max: 20 },
    optimal: { min: 0, max: 10 },
  },
  hematocrit: {
    normal: { min: 36, max: 50 },
    optimal: { min: 38, max: 48 },
  },
  platelets: {
    normal: { min: 150, max: 450 },
    optimal: { min: 180, max: 350 },
  },
  bun: {
    normal: { min: 7, max: 20 },
    optimal: { min: 10, max: 18 },
  },
  egfr: {
    normal: { min: 60, max: 150 },
    optimal: { min: 90, max: 120 },
  },
  chloride: {
    normal: { min: 98, max: 107 },
    optimal: { min: 100, max: 106 },
  },
  bicarbonate: {
    normal: { min: 22, max: 29 },
    optimal: { min: 24, max: 28 },
  },
  anionGap: {
    normal: { min: 3, max: 12 },
    optimal: { min: 6, max: 10 },
  },
  urineAlbuminCreatinineRatio: {
    normal: { min: 0, max: 30 },
    optimal: { min: 0, max: 10 },
  },
  // New biomarkers from Figma
  albumin: {
    normal: { min: 3.5, max: 5.5 },
    optimal: { min: 3.9, max: 5.0 },
  },
  calcium: {
    normal: { min: 8.5, max: 10.5 },
    optimal: { min: 8.8, max: 10.2 },
  },
  creatinine: {
    normal: { min: 0.6, max: 1.2 },
    optimal: { min: 0.7, max: 1.1 },
  },
  potassium: {
    normal: { min: 3.5, max: 5.5 },
    optimal: { min: 3.8, max: 5.0 },
  },
  uricAcid: {
    normal: { min: 2.5, max: 7.0 },
    optimal: { min: 3.0, max: 6.0 },
  },
  carbonDioxide: {
    normal: { min: 23, max: 29 },
    optimal: { min: 24, max: 28 },
  },
  sodium: {
    normal: { min: 136, max: 145 },
    optimal: { min: 137, max: 144 },
  },
  insulin: {
    normal: { min: 2.6, max: 24.9 },
    optimal: { min: 3.0, max: 15.0 },
  },
  magnesium: {
    normal: { min: 1.7, max: 2.2 },
    optimal: { min: 1.9, max: 2.2 },
  },
  folate: {
    normal: { min: 4, max: 20 },
    optimal: { min: 8, max: 20 },
  },
  iron: {
    normal: { min: 60, max: 170 },
    optimal: { min: 75, max: 150 },
  },
  ferritin: {
    normal: { min: 20, max: 300 },
    optimal: { min: 50, max: 150 },
  },
  vitaminB12: {
    normal: { min: 200, max: 900 },
    optimal: { min: 400, max: 900 },
  },
  zinc: {
    normal: { min: 60, max: 120 },
    optimal: { min: 80, max: 110 },
  },
  alphaTocopherol: {
    normal: { min: 5.5, max: 17 },
    optimal: { min: 8, max: 16 },
  },
  // Musculoskeletal - Bone Health
  alkalinePhosphatase: {
    normal: { min: 44, max: 147 },
    optimal: { min: 50, max: 120 },
  },
  phosphorus: {
    normal: { min: 2.5, max: 4.5 },
    optimal: { min: 2.8, max: 4.2 },
  },
  pth: {
    normal: { min: 15, max: 65 },
    optimal: { min: 20, max: 55 },
  },
  osteocalcin: {
    normal: { min: 9, max: 42 },
    optimal: { min: 12, max: 35 },
  },
  ctx: {
    normal: { min: 0.1, max: 0.85 },
    optimal: { min: 0.1, max: 0.5 },
  },
  p1np: {
    normal: { min: 15, max: 115 },
    optimal: { min: 20, max: 80 },
  },
  // Musculoskeletal - Muscle Health
  creatineKinase: {
    normal: { min: 30, max: 200 },
    optimal: { min: 40, max: 150 },
  },
  myoglobin: {
    normal: { min: 25, max: 72 },
    optimal: { min: 28, max: 60 },
  },
  ldh: {
    normal: { min: 140, max: 280 },
    optimal: { min: 150, max: 250 },
  },
  aldolase: {
    normal: { min: 1.0, max: 7.5 },
    optimal: { min: 1.5, max: 6.0 },
  },
  bilirubinTotal: {
    normal: { min: 0.1, max: 1.2 },
    optimal: { min: 0.2, max: 1.0 },
  },
  amylase: {
    normal: { min: 30, max: 110 },
    optimal: { min: 35, max: 100 },
  },
  lipase: {
    normal: { min: 0, max: 160 },
    optimal: { min: 10, max: 140 },
  },
  // Lipid panel — extended members. Standard adult defaults; a clinician can tune.
  vldlCholesterol: {
    normal: { min: 0, max: 40 },
    optimal: { min: 0, max: 30 },
  },
  nonHdlCholesterol: {
    normal: { min: 0, max: 160 },
    optimal: { min: 0, max: 130 },
  },
  cholesterolHdlRatio: {
    normal: { min: 0, max: 5 },
    optimal: { min: 0, max: 3.5 },
  },
  apolipoproteinB: {
    normal: { min: 0, max: 130 },
    optimal: { min: 0, max: 90 },
  },
  // CBC — red-cell indices.
  mcv: {
    normal: { min: 80, max: 100 },
    optimal: { min: 82, max: 98 },
  },
  mch: {
    normal: { min: 27, max: 33 },
    optimal: { min: 28, max: 32 },
  },
  mchc: {
    normal: { min: 32, max: 36 },
    optimal: { min: 33, max: 35 },
  },
  rdw: {
    normal: { min: 11.5, max: 14.5 },
    optimal: { min: 12, max: 14 },
  },
  mpv: {
    normal: { min: 7.5, max: 11.5 },
    optimal: { min: 8, max: 11 },
  },
  // CBC — differential (percentages).
  neutrophilsPct: {
    normal: { min: 40, max: 70 },
    optimal: { min: 45, max: 65 },
  },
  lymphocytesPct: {
    normal: { min: 20, max: 40 },
    optimal: { min: 22, max: 38 },
  },
  monocytesPct: {
    normal: { min: 2, max: 10 },
    optimal: { min: 2, max: 8 },
  },
  eosinophilsPct: {
    normal: { min: 0, max: 6 },
    optimal: { min: 0, max: 4 },
  },
  basophilsPct: {
    normal: { min: 0, max: 2 },
    optimal: { min: 0, max: 1 },
  },
  // CBC — differential (absolute counts, 10*3/uL).
  neutrophilsAbs: {
    normal: { min: 1.8, max: 7.7 },
    optimal: { min: 2.0, max: 7.0 },
  },
  lymphocytesAbs: {
    normal: { min: 1.0, max: 4.0 },
    optimal: { min: 1.2, max: 3.5 },
  },
  monocytesAbs: {
    normal: { min: 0.2, max: 0.95 },
    optimal: { min: 0.2, max: 0.8 },
  },
  eosinophilsAbs: {
    normal: { min: 0, max: 0.5 },
    optimal: { min: 0, max: 0.4 },
  },
  basophilsAbs: {
    normal: { min: 0, max: 0.2 },
    optimal: { min: 0, max: 0.1 },
  },
  // BMP / renal function.
  bunCreatinineRatio: {
    normal: { min: 10, max: 20 },
    optimal: { min: 10, max: 16 },
  },
  // Liver / hepatic function — extended members.
  ggt: {
    normal: { min: 0, max: 65 },
    optimal: { min: 0, max: 40 },
  },
  totalProtein: {
    normal: { min: 6.0, max: 8.3 },
    optimal: { min: 6.5, max: 8.0 },
  },
  globulin: {
    normal: { min: 2.0, max: 3.5 },
    optimal: { min: 2.3, max: 3.2 },
  },
  albuminGlobulinRatio: {
    normal: { min: 1.0, max: 2.5 },
    optimal: { min: 1.2, max: 2.2 },
  },
  bilirubinDirect: {
    normal: { min: 0, max: 0.3 },
    optimal: { min: 0, max: 0.2 },
  },
  // Thyroid — extended members.
  freeT3: {
    normal: { min: 2.3, max: 4.2 },
    optimal: { min: 3.0, max: 4.0 },
  },
  reverseT3: {
    normal: { min: 8, max: 25 },
    optimal: { min: 10, max: 24 },
  },
  // Iron studies — extended members.
  transferrinSaturation: {
    normal: { min: 20, max: 50 },
    optimal: { min: 25, max: 45 },
  },
};

// -----------------------------------------------------------------------------
// Biomarker Configuration
// -----------------------------------------------------------------------------

/**
 * Complete configuration for all supported biomarkers.
 * Keyed by LOINC code for O(1) lookup.
 */
export const BIOMARKER_CONFIG: Record<string, BiomarkerConfig> = {
  // ---------------------------------------------------------------------------
  // Cardiovascular (Wearable)
  // ---------------------------------------------------------------------------
  [VitalLOINCCodes.HRV.code]: {
    loincCode: VitalLOINCCodes.HRV.code,
    displayName: "Heart Rate Variability",
    shortName: "HRV",
    unit: "ms",
    category: "cardiovascular",
    modelClass: "HRVObservation",
    referenceRange: WEARABLE_RANGES.hrv,
    supportsCQ: true,
  },
  [VitalLOINCCodes.RestingHeartRate.code]: {
    loincCode: VitalLOINCCodes.RestingHeartRate.code,
    displayName: "Resting Heart Rate",
    shortName: "RHR",
    unit: "bpm",
    category: "cardiovascular",
    modelClass: "HeartRateObservation",
    referenceRange: WEARABLE_RANGES.restingHeartRate,
    supportsCQ: true,
  },
  [VitalLOINCCodes.HeartRate.code]: {
    loincCode: VitalLOINCCodes.HeartRate.code,
    displayName: "Heart Rate",
    shortName: "HR",
    unit: "bpm",
    category: "cardiovascular",
    modelClass: "HeartRateObservation",
    referenceRange: null, // General HR is context-dependent; use RHR for at-rest ranges
    supportsCQ: true,
  },

  // ---------------------------------------------------------------------------
  // Cardiovascular - Respiratory Metrics (Wearable)
  // ---------------------------------------------------------------------------
  [VitalLOINCCodes.BloodOxygen.code]: {
    loincCode: VitalLOINCCodes.BloodOxygen.code,
    displayName: "Blood Oxygen Saturation",
    shortName: "SpO2",
    unit: "%",
    category: "cardiovascular",
    modelClass: "BloodOxygenObservation",
    referenceRange: WEARABLE_RANGES.bloodOxygen,
    supportsCQ: true,
  },
  [VitalLOINCCodes.RespiratoryRate.code]: {
    loincCode: VitalLOINCCodes.RespiratoryRate.code,
    displayName: "Respiratory Rate",
    shortName: "RR",
    unit: "/min",
    category: "autonomic",
    modelClass: "RespiratoryRateObservation",
    referenceRange: null,
    supportsCQ: true,
  },
  [VitalLOINCCodes.BloodPressureSystolic.code]: {
    loincCode: VitalLOINCCodes.BloodPressureSystolic.code,
    displayName: "Systolic Blood Pressure",
    shortName: "SBP",
    unit: "mmHg",
    category: "cardiovascular",
    modelClass: "BloodPressureSystolicObservation",
    referenceRange: WEARABLE_RANGES.bloodPressureSystolic,
    supportsCQ: true,
  },
  [VitalLOINCCodes.BloodPressureDiastolic.code]: {
    loincCode: VitalLOINCCodes.BloodPressureDiastolic.code,
    displayName: "Diastolic Blood Pressure",
    shortName: "DBP",
    unit: "mmHg",
    category: "cardiovascular",
    modelClass: "BloodPressureDiastolicObservation",
    referenceRange: WEARABLE_RANGES.bloodPressureDiastolic,
    supportsCQ: true,
  },
  [VitalLOINCCodes.BodyTemperature.code]: {
    loincCode: VitalLOINCCodes.BodyTemperature.code,
    displayName: "Body Temperature",
    shortName: "Temp",
    unit: "°C",
    category: "autonomic",
    modelClass: "BodyTemperatureObservation",
    referenceRange: WEARABLE_RANGES.bodyTemperature,
    supportsCQ: true,
  },

  // ---------------------------------------------------------------------------
  // Sleep (Wearable)
  // ---------------------------------------------------------------------------
  [VitalLOINCCodes.SleepDuration.code]: {
    loincCode: VitalLOINCCodes.SleepDuration.code,
    displayName: "Sleep Duration",
    shortName: "Sleep",
    unit: "min",
    category: "sleep",
    modelClass: "SleepObservation",
    referenceRange: WEARABLE_RANGES.sleepDuration,
    // ENG-4411: Sleep Duration is a continuous daily wearable (renders as a hero card, like Sleep
    // Efficiency), so `supportsCQ` stays true — the client routes daily metrics to cards vs. sparse/
    // lab metrics to the table on this flag. It is intentionally NOT sourced from the CQ: the CQ's
    // `duration_second` is TIME IN BED (over-reports vs Oura's "Total Sleep"), so it is excluded from
    // the CQ column/LOINC maps (lib/trends/cq-utils.ts, cq-to-medplum.ts) and the CQ definition
    // (scripts/wearables/setup-continuous-queries.ts). Wearable ingestion (time ASLEEP = deep + light
    // + REM) is the single source of truth, served from FHIR.
    supportsCQ: true,
  },
  [VitalLOINCCodes.SleepEfficiency.code]: {
    loincCode: VitalLOINCCodes.SleepEfficiency.code,
    displayName: "Sleep Efficiency",
    shortName: "Efficiency",
    unit: "%",
    category: "sleep",
    modelClass: "SleepObservation",
    referenceRange: WEARABLE_RANGES.sleepEfficiency,
    supportsCQ: true,
  },

  // ---------------------------------------------------------------------------
  // Metabolism - Activity (Wearable)
  // ---------------------------------------------------------------------------
  [VitalLOINCCodes.Steps.code]: {
    loincCode: VitalLOINCCodes.Steps.code,
    displayName: "Daily Steps",
    shortName: "Steps",
    unit: "steps",
    category: "metabolism",
    modelClass: "StepsObservation",
    referenceRange: WEARABLE_RANGES.steps,
    supportsCQ: true,
    chartType: "bar",
  },
  [VitalLOINCCodes.ActiveCalories.code]: {
    loincCode: VitalLOINCCodes.ActiveCalories.code,
    displayName: "Active Calories",
    shortName: "Cal",
    unit: "kcal",
    category: "metabolism",
    modelClass: "CaloriesObservation",
    referenceRange: null,
    supportsCQ: true,
    chartType: "bar",
  },
  [VitalLOINCCodes.Distance.code]: {
    loincCode: VitalLOINCCodes.Distance.code,
    displayName: "Distance",
    shortName: "Dist",
    unit: "m",
    category: "metabolism",
    modelClass: "DistanceObservation",
    referenceRange: null,
    supportsCQ: true,
    chartType: "bar",
  },

  // ---------------------------------------------------------------------------
  // Musculoskeletal - Fitness (Wearable)
  // ---------------------------------------------------------------------------
  [VitalLOINCCodes.VO2Max.code]: {
    loincCode: VitalLOINCCodes.VO2Max.code,
    displayName: "VO₂ Max",
    shortName: "VO2",
    unit: "mL/kg/min",
    category: "muscular_skeletal",
    modelClass: null,
    referenceRange: {
      normal: { min: 20, max: 60 },
      optimal: { min: 35, max: 55 },
    },
    supportsCQ: false, // Sparse data, not daily
  },

  // ---------------------------------------------------------------------------
  // Metabolism - Body Composition (Wearable)
  // ---------------------------------------------------------------------------
  [VitalLOINCCodes.BodyHeight.code]: {
    loincCode: VitalLOINCCodes.BodyHeight.code,
    displayName: "Body Height",
    shortName: "Height",
    unit: "in",
    category: "metabolism",
    modelClass: "BodyHeightObservation",
    referenceRange: null,
    supportsCQ: false, // Sparse data
  },
  [VitalLOINCCodes.BodyWeight.code]: {
    loincCode: VitalLOINCCodes.BodyWeight.code,
    displayName: "Body Weight",
    shortName: "Weight",
    unit: "kg",
    category: "metabolism",
    modelClass: "BodyWeightObservation",
    referenceRange: WEARABLE_RANGES.bodyWeight,
    supportsCQ: true, // Connected scales report daily — render as a continuous device chart
  },
  [VitalLOINCCodes.BMI.code]: {
    loincCode: VitalLOINCCodes.BMI.code,
    displayName: "Body Mass Index",
    shortName: "BMI",
    unit: "kg/m²",
    category: "metabolism",
    modelClass: "BMIObservation",
    referenceRange: null,
    supportsCQ: true, // Connected scales report daily — render as a continuous device chart
  },
  [VitalLOINCCodes.BodyFat.code]: {
    loincCode: VitalLOINCCodes.BodyFat.code,
    displayName: "Body Fat Percentage",
    shortName: "Body Fat",
    unit: "%",
    category: "metabolism",
    modelClass: "BodyFatObservation",
    referenceRange: null,
    supportsCQ: false,
  },
  [VitalLOINCCodes.LeanBodyMass.code]: {
    loincCode: VitalLOINCCodes.LeanBodyMass.code,
    displayName: "Lean Body Mass",
    shortName: "LBM",
    unit: "kg",
    category: "muscular_skeletal",
    modelClass: "LeanBodyMassObservation",
    referenceRange: null, // Highly variable by individual/DEXA vs scales
    supportsCQ: true,
  },
  [VitalLOINCCodes.BoneMass.code]: {
    loincCode: VitalLOINCCodes.BoneMass.code,
    displayName: "Bone Mineral Content",
    shortName: "Bone",
    unit: "kg",
    category: "muscular_skeletal",
    modelClass: "BoneMassObservation",
    referenceRange: null, // Variable by individual and measurement method
    supportsCQ: true,
  },

  // ---------------------------------------------------------------------------
  // Metabolism - Glucose (Wearable + Lab)
  // ---------------------------------------------------------------------------
  [VitalLOINCCodes.GlucoseBlood.code]: {
    loincCode: VitalLOINCCodes.GlucoseBlood.code,
    displayName: "Blood Glucose",
    shortName: "Glucose",
    unit: "mg/dL",
    category: "metabolism",
    modelClass: "GlucoseObservation",
    referenceRange: WEARABLE_RANGES.glucose,
    supportsCQ: true,
    // NOTE: no textSearchTerms — "Glucose" alone is too broad (matches CGM hourly
    // stats like "Monday - Max Glucose"). Lab glucose is handled by fasting glucose
    // config (2345-7) and dynamic discovery.
  },
  [VitalLOINCCodes.GlucoseInterstitial.code]: {
    loincCode: VitalLOINCCodes.GlucoseInterstitial.code,
    displayName: "Interstitial Glucose (CGM)",
    shortName: "CGM",
    unit: "mg/dL",
    category: "metabolism",
    modelClass: "GlucoseObservation",
    referenceRange: WEARABLE_RANGES.glucose,
    supportsCQ: true,
    equivalentLoincCodes: ["97507-8"],
    // PDF-parsed CGM reports store aggregate summaries with these display names
    textSearchTerms: ["Average Glucose", "Average glucose"],
  },
  "90916-8": {
    loincCode: "90916-8",
    displayName: "Arachidonic Acid",
    shortName: "AA",
    unit: "%",
    category: "metabolism",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["88994-9"],
  },
  "90914-3": {
    loincCode: "90914-3",
    displayName: "DHA",
    shortName: "DHA",
    unit: "%",
    category: "metabolism",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["88995-6"],
  },
  "90912-7": {
    loincCode: "90912-7",
    displayName: "EPA",
    shortName: "EPA",
    unit: "%",
    category: "metabolism",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["88997-2"],
  },
  "90910-1": {
    loincCode: "90910-1",
    displayName: "Omega-6 / Omega-3 Ratio",
    shortName: "Omega 6:3",
    unit: "",
    category: "metabolism",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["48382-6"],
  },

  // ---------------------------------------------------------------------------
  // Lab Results - Lipid Panel (Cardiovascular)
  // ---------------------------------------------------------------------------
  "2093-3": {
    loincCode: "2093-3",
    displayName: "Total Cholesterol",
    shortName: "TC",
    unit: "mg/dL",
    category: "cardiovascular",
    panel: "lipid",
    modelClass: null,
    referenceRange: LAB_RANGES.totalCholesterol,
    supportsCQ: false,
    isLabResult: true,
    // Common serum/plasma equivalents seen in imported lab history. NOT 2083-4 — that's
    // "Cholesterol esterase" (an enzyme, U/L), a different analyte despite its misleading synonyms.
    equivalentLoincCodes: ["105515-1"],
    textSearchTerms: ["Cholesterol, Total", "Total Cholesterol"],
  },
  "2085-9": {
    loincCode: "2085-9",
    displayName: "HDL Cholesterol",
    shortName: "HDL",
    unit: "mg/dL",
    category: "cardiovascular",
    panel: "lipid",
    modelClass: null,
    referenceRange: LAB_RANGES.hdlCholesterol,
    supportsCQ: false,
    isLabResult: true,
    // TEMPORARY — MUST BACKFILL. 18263-7 is NOT a real LOINC (invented as "HDL Direct").
    // Real HDL (incl. direct assay) is 2085-9. Fold hides bad Trends rows until Obs are recoded.
    // REMOVE this equivalent once backfill completes.
    equivalentLoincCodes: ["18263-7"],
    textSearchTerms: ["HDL Cholesterol", "HDL", "HDL-C, Direct", "HDL Direct", "HDL-C Direct"],
  },
  "2089-1": {
    loincCode: "2089-1",
    displayName: "LDL Cholesterol",
    shortName: "LDL",
    unit: "mg/dL",
    category: "cardiovascular",
    panel: "lipid",
    modelClass: null,
    referenceRange: LAB_RANGES.ldlCholesterol,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["13457-7", "18262-6", "18262-9", "96259-7"],
    textSearchTerms: ["LDL Chol Calc", "LDL Cholesterol", "LDL-C Direct", "LDL-CHOLESTEROL", "LDL"],
  },
  "11054-4": {
    loincCode: "11054-4",
    displayName: "LDL/HDL Ratio",
    shortName: "LDL/HDL",
    unit: "",
    category: "cardiovascular",
    panel: "lipid",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["44717-7"],
  },
  "2571-8": {
    loincCode: "2571-8",
    displayName: "Triglycerides",
    shortName: "TG",
    unit: "mg/dL",
    category: "cardiovascular",
    panel: "lipid",
    modelClass: null,
    referenceRange: LAB_RANGES.triglycerides,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Triglycerides"],
  },
  "13458-5": {
    loincCode: "13458-5",
    displayName: "VLDL Cholesterol",
    shortName: "VLDL",
    unit: "mg/dL",
    category: "cardiovascular",
    panel: "lipid",
    modelClass: null,
    referenceRange: LAB_RANGES.vldlCholesterol,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["VLDL", "VLDL Cholesterol", "VLDL Chol"],
  },
  "43396-1": {
    loincCode: "43396-1",
    displayName: "Non-HDL Cholesterol",
    shortName: "Non-HDL",
    unit: "mg/dL",
    category: "cardiovascular",
    panel: "lipid",
    modelClass: null,
    referenceRange: LAB_RANGES.nonHdlCholesterol,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Non-HDL Cholesterol", "Non HDL Cholesterol", "Non-HDL"],
  },
  "9830-1": {
    loincCode: "9830-1",
    displayName: "Cholesterol/HDL Ratio",
    detailDisplayName: "Total Cholesterol / HDL Ratio",
    shortName: "Chol/HDL",
    unit: "ratio",
    category: "cardiovascular",
    panel: "lipid",
    modelClass: null,
    referenceRange: LAB_RANGES.cholesterolHdlRatio,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Cholesterol/HDL Ratio", "Chol/HDL Ratio", "Chol/HDLC Ratio"],
  },
  "1884-6": {
    loincCode: "1884-6",
    displayName: "Apolipoprotein B",
    shortName: "ApoB",
    unit: "mg/dL",
    category: "cardiovascular",
    panel: "lipid",
    modelClass: null,
    referenceRange: LAB_RANGES.apolipoproteinB,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Apolipoprotein B", "Apo B", "ApoB"],
  },
  "10835-7": {
    loincCode: "10835-7",
    displayName: "Lipoprotein (a)",
    shortName: "Lp(a)",
    unit: "nmol/L",
    category: "cardiovascular",
    panel: "lipid",
    modelClass: null,
    // Reported in mg/dL or nmol/L with assay-specific cutoffs; defer to the lab's range.
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    // 49748-7 (Lipoprotein.alpha / HDL) is a DIFFERENT analyte — do not fold it in.
    equivalentLoincCodes: ["43583-4"],
    textSearchTerms: ["Lipoprotein (a)", "Lipoprotein a", "Lp(a)", "Lpa"],
  },

  // ---------------------------------------------------------------------------
  // Lab Results - Cardiac Markers (Cardiovascular)
  // ---------------------------------------------------------------------------
  "10839-9": {
    loincCode: "10839-9",
    displayName: "Troponin",
    detailDisplayName: "Troponin I/T",
    shortName: "Troponin",
    unit: "ng/L",
    category: "cardiovascular",
    panel: "cardiac",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["6598-7"],
    textSearchTerms: ["Troponin", "Troponin I", "Troponin T", "Trop I", "TnT"],
  },
  "33762-6": {
    loincCode: "33762-6",
    displayName: "BNP / NT-proBNP",
    detailDisplayName: "B-type Natriuretic Peptide / NT-proBNP",
    shortName: "BNP",
    unit: "pg/mL",
    category: "cardiovascular",
    panel: "cardiac",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["30934-4"],
    textSearchTerms: ["BNP", "NT-proBNP", "Natriuretic peptide"],
  },
  "13965-9": {
    loincCode: "13965-9",
    displayName: "Homocysteine",
    shortName: "Homocysteine",
    unit: "umol/L",
    category: "cardiovascular",
    panel: "cardiac",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
  },

  // ---------------------------------------------------------------------------
  // Lab Results - CBC (Complete Blood Count)
  // ---------------------------------------------------------------------------
  "6690-2": {
    loincCode: "6690-2",
    displayName: "White Blood Cells",
    shortName: "WBC",
    unit: "10*3/uL",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.wbc,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["WBC"],
  },
  "4544-3": {
    loincCode: "4544-3",
    displayName: "Hematocrit",
    shortName: "Hct",
    unit: "%",
    category: "nutrients",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.hematocrit,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Hematocrit", "HCT"],
  },
  "777-3": {
    loincCode: "777-3",
    displayName: "Platelets",
    shortName: "Plt",
    unit: "10*3/uL",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.platelets,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["26515-7"],
    textSearchTerms: ["Platelets", "Platelet Count", "PLT"],
  },
  "789-8": {
    loincCode: "789-8",
    displayName: "Red Blood Cells",
    shortName: "RBC",
    unit: "10*6/uL",
    category: "nutrients",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.rbc,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["RBC"],
  },
  "718-7": {
    loincCode: "718-7",
    displayName: "Hemoglobin",
    shortName: "Hgb",
    unit: "g/dL",
    category: "nutrients",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.hemoglobin,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Hemoglobin"],
  },
  // CBC red-cell indices
  "787-2": {
    loincCode: "787-2",
    displayName: "MCV",
    detailDisplayName: "Mean Corpuscular Volume",
    shortName: "MCV",
    unit: "fL",
    category: "nutrients",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.mcv,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["30428-7"],
    textSearchTerms: ["MCV", "Mean Corpuscular Volume"],
  },
  "785-6": {
    loincCode: "785-6",
    displayName: "MCH",
    detailDisplayName: "Mean Corpuscular Hemoglobin",
    shortName: "MCH",
    unit: "pg",
    category: "nutrients",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.mch,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["MCH", "Mean Corpuscular Hemoglobin"],
  },
  "786-4": {
    loincCode: "786-4",
    displayName: "MCHC",
    detailDisplayName: "Mean Corpuscular Hemoglobin Concentration",
    shortName: "MCHC",
    unit: "g/dL",
    category: "nutrients",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.mchc,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["28540-3"],
    textSearchTerms: ["MCHC", "Mean Corpuscular Hemoglobin Concentration"],
  },
  "788-0": {
    loincCode: "788-0",
    displayName: "RDW",
    detailDisplayName: "Red Cell Distribution Width",
    shortName: "RDW",
    unit: "%",
    category: "nutrients",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.rdw,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["RDW", "Red Cell Distribution Width"],
  },
  "32623-1": {
    loincCode: "32623-1",
    displayName: "MPV",
    detailDisplayName: "Mean Platelet Volume",
    shortName: "MPV",
    unit: "fL",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.mpv,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["MPV", "Mean Platelet Volume"],
  },
  // CBC differential — percentages
  "770-8": {
    loincCode: "770-8",
    displayName: "Neutrophils %",
    shortName: "Neut %",
    unit: "%",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.neutrophilsPct,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["71676-1"],
    textSearchTerms: ["Neutrophil %", "Neutrophils %", "Neutrophils (%)"],
  },
  "736-9": {
    loincCode: "736-9",
    displayName: "Lymphocytes %",
    shortName: "Lymph %",
    unit: "%",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.lymphocytesPct,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["71678-7"],
    textSearchTerms: ["Lymphocyte %", "Lymphs %", "Lymphocytes %"],
  },
  "5905-5": {
    loincCode: "5905-5",
    displayName: "Monocytes %",
    shortName: "Mono %",
    unit: "%",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.monocytesPct,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["26485-3"],
    textSearchTerms: ["Monocyte %", "Monos %", "Monocytes %"],
  },
  "38518-7": {
    loincCode: "38518-7",
    displayName: "Immature Granulocytes %",
    shortName: "Imm Gran %",
    unit: "%",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["71695-1"],
  },
  "713-8": {
    loincCode: "713-8",
    displayName: "Eosinophils %",
    shortName: "Eos %",
    unit: "%",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.eosinophilsPct,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["26450-7"],
    textSearchTerms: ["Eosinophil %", "Eos %", "Eosinophils %", "Eosinophils % of Leukocytes"],
  },
  "706-2": {
    loincCode: "706-2",
    displayName: "Basophils %",
    shortName: "Baso %",
    unit: "%",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.basophilsPct,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Basophil %", "Basos %", "Basophils %"],
  },
  // CBC differential — absolute counts
  "751-8": {
    loincCode: "751-8",
    displayName: "Neutrophils (Absolute)",
    shortName: "Neut Abs",
    unit: "10*3/uL",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.neutrophilsAbs,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Neutrophils Absolute", "Absolute Neutrophils", "ANC"],
  },
  "731-0": {
    loincCode: "731-0",
    displayName: "Lymphocytes (Absolute)",
    shortName: "Lymph Abs",
    unit: "10*3/uL",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.lymphocytesAbs,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["26474-7"],
    textSearchTerms: ["Lymphocytes Absolute", "Absolute Lymphocytes"],
  },
  "742-7": {
    loincCode: "742-7",
    displayName: "Monocytes (Absolute)",
    shortName: "Mono Abs",
    unit: "10*3/uL",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.monocytesAbs,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["26484-6"],
    textSearchTerms: ["Monocytes Absolute", "Absolute Monocytes"],
  },
  "51584-1": {
    loincCode: "51584-1",
    displayName: "Immature Granulocytes (Absolute)",
    shortName: "Imm Gran Abs",
    unit: "10*3/uL",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["53115-2"],
  },
  "711-2": {
    loincCode: "711-2",
    displayName: "Eosinophils (Absolute)",
    shortName: "Eos Abs",
    unit: "10*3/uL",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.eosinophilsAbs,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["26449-9"],
    textSearchTerms: ["Eosinophils Absolute", "Absolute Eosinophils"],
  },
  "704-7": {
    loincCode: "704-7",
    displayName: "Basophils (Absolute)",
    shortName: "Baso Abs",
    unit: "10*3/uL",
    category: "inflammation",
    panel: "cbc",
    modelClass: null,
    referenceRange: LAB_RANGES.basophilsAbs,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Basophils Absolute", "Absolute Basophils"],
  },

  // ---------------------------------------------------------------------------
  // Lab Results - Liver (Digestive)
  // ---------------------------------------------------------------------------
  "1742-6": {
    loincCode: "1742-6",
    displayName: "ALT (Alanine Aminotransferase)",
    shortName: "ALT",
    unit: "U/L",
    category: "digestive",
    panel: "liver",
    modelClass: null,
    referenceRange: LAB_RANGES.alt,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["ALT"],
  },
  "1920-8": {
    loincCode: "1920-8",
    displayName: "AST (Aspartate Aminotransferase)",
    shortName: "AST",
    unit: "U/L",
    category: "digestive",
    panel: "liver",
    modelClass: null,
    referenceRange: LAB_RANGES.ast,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["AST"],
  },
  "1975-2": {
    loincCode: "1975-2",
    displayName: "Total Bilirubin",
    shortName: "Bili",
    unit: "mg/dL",
    category: "digestive",
    panel: "liver",
    modelClass: null,
    referenceRange: LAB_RANGES.bilirubinTotal,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Bilirubin", "Total Bilirubin"],
  },
  "1968-7": {
    loincCode: "1968-7",
    displayName: "Direct Bilirubin",
    detailDisplayName: "Bilirubin, Direct (Conjugated)",
    shortName: "Direct Bili",
    unit: "mg/dL",
    category: "digestive",
    panel: "liver",
    modelClass: null,
    referenceRange: LAB_RANGES.bilirubinDirect,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Direct Bilirubin", "Bilirubin Direct", "Conjugated Bilirubin"],
  },
  "2324-2": {
    loincCode: "2324-2",
    displayName: "GGT",
    detailDisplayName: "Gamma-Glutamyl Transferase",
    shortName: "GGT",
    unit: "U/L",
    category: "digestive",
    panel: "liver",
    modelClass: null,
    referenceRange: LAB_RANGES.ggt,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["GGT", "Gamma-Glutamyl Transferase", "Gamma GT"],
  },
  "2885-2": {
    loincCode: "2885-2",
    displayName: "Total Protein",
    shortName: "Total Protein",
    unit: "g/dL",
    category: "nutrients",
    panel: "liver",
    modelClass: null,
    referenceRange: LAB_RANGES.totalProtein,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Total Protein", "Protein, Total"],
  },
  "10834-0": {
    loincCode: "10834-0",
    displayName: "Globulin",
    detailDisplayName: "Globulin, Total (Calculated)",
    shortName: "Globulin",
    unit: "g/dL",
    category: "nutrients",
    panel: "liver",
    modelClass: null,
    referenceRange: LAB_RANGES.globulin,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Globulin", "Globulin Total"],
  },
  "1759-0": {
    loincCode: "1759-0",
    displayName: "Albumin/Globulin Ratio",
    shortName: "A/G Ratio",
    unit: "ratio",
    category: "nutrients",
    panel: "liver",
    modelClass: null,
    referenceRange: LAB_RANGES.albuminGlobulinRatio,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["A/G Ratio", "Albumin/Globulin Ratio", "Albumin Globulin Ratio"],
  },
  "1798-8": {
    loincCode: "1798-8",
    displayName: "Amylase",
    shortName: "Amylase",
    unit: "U/L",
    category: "digestive",
    panel: "pancreatic",
    modelClass: null,
    referenceRange: LAB_RANGES.amylase,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Amylase"],
  },
  "3040-3": {
    loincCode: "3040-3",
    displayName: "Lipase",
    shortName: "Lipase",
    unit: "U/L",
    category: "digestive",
    panel: "pancreatic",
    modelClass: null,
    referenceRange: LAB_RANGES.lipase,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Lipase"],
  },

  // ---------------------------------------------------------------------------
  // Lab Results - Endocrine
  // ---------------------------------------------------------------------------
  "3016-3": {
    loincCode: "3016-3",
    displayName: "Thyroid Stimulating Hormone",
    shortName: "TSH",
    unit: "mIU/L",
    category: "endocrine",
    panel: "thyroid",
    modelClass: null,
    referenceRange: LAB_RANGES.tsh,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["TSH", "Thyroid Stimulating Hormone"],
  },
  "1989-3": {
    loincCode: "1989-3",
    displayName: "Vitamin D",
    shortName: "Vit D",
    unit: "ng/mL",
    // Genuinely dual-natured (a hormone AND a fat-soluble vitamin), so it fits neither `endocrine`
    // nor `nutrients` cleanly — parked in `other` rather than forcing an arbitrary pick. `panel`
    // stays `vitamins`.
    category: "other",
    panel: "vitamins",
    modelClass: null,
    referenceRange: LAB_RANGES.vitaminD,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["62292-8", "83070-3", "83071-1"],
    textSearchTerms: ["Vitamin D"],
  },
  "49054-0": {
    loincCode: "49054-0",
    displayName: "25-Hydroxyvitamin D2",
    shortName: "Vit D2",
    unit: "ng/mL",
    category: "other",
    panel: "vitamins",
    modelClass: null,
    // D2 is a component analyte — don't reuse total 25-OH D (D2+D3) ranges from 1989-3.
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    // TEMPORARY — MUST BACKFILL. 17970-4 is NOT a real LOINC.
    // Real 25-OH Vitamin D2 is 49054-0. Fold hides bad Trends rows until Obs are recoded.
    // REMOVE this equivalent once backfill completes.
    equivalentLoincCodes: ["17970-4"],
    textSearchTerms: ["25-OH Vitamin D2", "25-Hydroxyvitamin D2", "Vitamin D2"],
  },
  "3024-7": {
    loincCode: "3024-7",
    displayName: "Free T4",
    shortName: "Free T4",
    unit: "ng/dL",
    category: "endocrine",
    panel: "thyroid",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["14920-3", "111274-7"],
    textSearchTerms: ["Free T4", "Thyroxine Free"],
  },
  "3026-2": {
    loincCode: "3026-2",
    displayName: "Total T4",
    shortName: "T4",
    unit: "ug/dL",
    category: "endocrine",
    panel: "thyroid",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Total T4", "Thyroxine", "T4 Total"],
  },
  "8098-6": {
    loincCode: "8098-6",
    displayName: "Thyroglobulin Ab",
    shortName: "Tg Ab",
    unit: "U/mL",
    category: "endocrine",
    panel: "thyroid",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["56536-6"],
  },
  "3053-6": {
    loincCode: "3053-6",
    displayName: "T3",
    detailDisplayName: "Total T3",
    shortName: "T3",
    unit: "ng/dL",
    category: "endocrine",
    panel: "thyroid",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Total T3", "Triiodothyronine", "T3 Total"],
  },
  "3051-0": {
    loincCode: "3051-0",
    displayName: "Free T3",
    detailDisplayName: "Triiodothyronine, Free",
    shortName: "Free T3",
    unit: "pg/mL",
    category: "endocrine",
    panel: "thyroid",
    modelClass: null,
    referenceRange: LAB_RANGES.freeT3,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Free T3", "Triiodothyronine Free", "FT3"],
  },
  "30924-5": {
    loincCode: "30924-5",
    displayName: "Reverse T3",
    detailDisplayName: "Reverse Triiodothyronine",
    shortName: "rT3",
    unit: "ng/dL",
    category: "endocrine",
    panel: "thyroid",
    modelClass: null,
    referenceRange: LAB_RANGES.reverseT3,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Reverse T3", "rT3", "Reverse Triiodothyronine"],
  },

  // ---------------------------------------------------------------------------
  // Lab Results - Reproductive Hormones (Endocrine)
  // ---------------------------------------------------------------------------
  "15067-2": {
    loincCode: "15067-2",
    displayName: "Follicle-Stimulating Hormone",
    shortName: "FSH",
    unit: "mIU/mL",
    category: "endocrine",
    panel: "hormones",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["FSH", "Follicle Stimulating Hormone", "Follitropin"],
  },
  "10501-5": {
    loincCode: "10501-5",
    displayName: "Luteinizing Hormone",
    shortName: "LH",
    unit: "mIU/mL",
    category: "endocrine",
    panel: "hormones",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["LH", "Luteinizing Hormone", "Lutropin"],
  },
  "2842-3": {
    loincCode: "2842-3",
    displayName: "Prolactin",
    shortName: "PRL",
    unit: "ng/mL",
    category: "endocrine",
    panel: "hormones",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Prolactin"],
  },
  "2986-8": {
    loincCode: "2986-8",
    displayName: "Total Testosterone",
    shortName: "Testosterone",
    unit: "ng/dL",
    category: "endocrine",
    panel: "hormones",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Total Testosterone", "Testosterone"],
  },
  "2991-8": {
    loincCode: "2991-8",
    displayName: "Free Testosterone",
    shortName: "Free T",
    unit: "pg/mL",
    category: "endocrine",
    panel: "hormones",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["103227-5"],
    textSearchTerms: ["Free Testosterone", "Testosterone Free"],
  },
  // Distinct from Total (2986-8) and Free (2991-8) — three separately-ordered analytes.
  // 108779-0's own LOINC synonym list carries "Testosterone.bioavailable", so it must never
  // become an equivalentLoincCode of Free Testosterone.
  "108779-0": {
    loincCode: "108779-0",
    displayName: "Bioavailable Testosterone",
    shortName: "Bioavail T",
    unit: "ng/dL",
    category: "endocrine",
    panel: "hormones",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Bioavailable Testosterone", "Testosterone Bioavailable", "Bioavailable T"],
  },
  "2839-9": {
    loincCode: "2839-9",
    displayName: "Progesterone",
    shortName: "Prog",
    unit: "ng/mL",
    category: "endocrine",
    panel: "hormones",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Progesterone"],
  },
  "2243-4": {
    loincCode: "2243-4",
    displayName: "Estradiol",
    shortName: "E2",
    unit: "pg/mL",
    category: "endocrine",
    panel: "hormones",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Estradiol", "E2"],
  },
  "38476-8": {
    loincCode: "38476-8",
    displayName: "Anti-Mullerian Hormone",
    shortName: "AMH",
    unit: "ng/mL",
    category: "endocrine",
    panel: "hormones",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["AMH", "Anti-Mullerian Hormone", "Mullerian inhibiting substance"],
  },
  "2191-5": {
    loincCode: "2191-5",
    displayName: "DHEA-S",
    detailDisplayName: "Dehydroepiandrosterone Sulfate",
    shortName: "DHEA-S",
    unit: "ug/dL",
    category: "endocrine",
    panel: "hormones",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["DHEA-S", "DHEAS", "Dehydroepiandrosterone sulfate"],
  },
  "13967-5": {
    loincCode: "13967-5",
    displayName: "Sex Hormone-Binding Globulin",
    shortName: "SHBG",
    unit: "nmol/L",
    category: "endocrine",
    panel: "hormones",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["SHBG", "Sex Hormone-Binding Globulin"],
  },

  // ---------------------------------------------------------------------------
  // Lab Results - Glucose / Diabetes (Metabolism)
  // ---------------------------------------------------------------------------
  "1558-6": {
    loincCode: "1558-6",
    displayName: "Fasting Glucose",
    shortName: "FG",
    unit: "mg/dL",
    category: "metabolism",
    panel: "bmp",
    modelClass: null,
    referenceRange: LAB_RANGES.fastingGlucose,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Fasting Glucose", "Glucose, Fasting"],
  },
  "4548-4": {
    loincCode: "4548-4",
    displayName: "Hemoglobin A1c",
    shortName: "HbA1c",
    unit: "%",
    category: "metabolism",
    panel: "glycemic",
    modelClass: null,
    referenceRange: LAB_RANGES.hba1c,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Hemoglobin A1c", "HbA1c", "A1C"],
  },

  // ---------------------------------------------------------------------------
  // Lab Results - Inflammatory Markers (Inflammation)
  // ---------------------------------------------------------------------------
  "1988-5": {
    loincCode: "1988-5",
    displayName: "C-Reactive Protein",
    shortName: "CRP",
    unit: "mg/L",
    category: "inflammation",
    panel: "inflammation",
    modelClass: null,
    referenceRange: LAB_RANGES.crp,
    supportsCQ: false,
    isLabResult: true,
    // Quest and AI-parsed high-sensitivity CRP currently land under these mass-based codes.
    equivalentLoincCodes: ["30522-7", "71426-1"],
    textSearchTerms: ["C-Reactive Protein", "CRP", "High-Sensitivity CRP", "hs-CRP", "hsCRP"],
  },
  "4537-7": {
    loincCode: "4537-7",
    displayName: "Erythrocyte Sedimentation Rate",
    shortName: "ESR",
    unit: "mm/hr",
    category: "inflammation",
    panel: "inflammation",
    modelClass: null,
    referenceRange: LAB_RANGES.esr,
    supportsCQ: false,
    isLabResult: true,
    // Methodless ESR in Blood (30341-2) is the same analyte as Westergren 4537-7.
    equivalentLoincCodes: ["30341-2"],
    textSearchTerms: ["ESR", "Sedimentation Rate", "ESR (Blood)", "ESR(SED-RATE)"],
  },
  "5048-4": {
    loincCode: "5048-4",
    displayName: "Antinuclear Antibody",
    shortName: "ANA",
    unit: "",
    // Autoimmune autoantibodies are disease diagnostics (lupus/RA), not a trendable inflammation
    // marker — same "diagnostic serology → other" rule as the infectious panel.
    category: "other",
    panel: "autoimmune",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["ANA", "Antinuclear Antibody", "Nuclear Ab"],
  },
  "5198-7": {
    loincCode: "5198-7",
    displayName: "Hepatitis C Antibody",
    shortName: "HCV Ab",
    unit: "",
    // Infectious-disease serology, not a functional system — qualitative diagnostic, not a trend.
    category: "other",
    panel: "infectious",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["13955-0", "16128-1"],
    textSearchTerms: ["HCV Ab", "Hepatitis C Antibody"],
  },
  "33959-8": {
    loincCode: "33959-8",
    displayName: "Procalcitonin",
    shortName: "PCT",
    unit: "ng/mL",
    category: "inflammation",
    panel: "inflammation",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Procalcitonin"],
  },
  "26881-3": {
    loincCode: "26881-3",
    displayName: "Interleukin-6",
    shortName: "IL-6",
    unit: "pg/mL",
    category: "inflammation",
    panel: "inflammation",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["IL-6", "Interleukin 6", "Interleukin-6"],
  },
  "8123-2": {
    loincCode: "8123-2",
    displayName: "CD4 Cells",
    shortName: "CD4",
    unit: "%",
    // Immune-status monitoring (HIV/immunodeficiency), not a general inflammation trend → other.
    category: "other",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["CD4", "T4 helper"],
  },
  "20402-4": {
    loincCode: "20402-4",
    displayName: "Natural Killer Cells",
    shortName: "NK Cells",
    unit: "cells/uL",
    // Immune-status monitoring, not a general inflammation trend → other.
    category: "other",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["18267-5"],
    textSearchTerms: ["Natural Killer", "NK Cells", "CD16+CD56"],
  },
  "11572-5": {
    loincCode: "11572-5",
    displayName: "Rheumatoid Factor",
    shortName: "RF",
    unit: "IU/mL",
    // Autoimmune autoantibodies are disease diagnostics (lupus/RA), not a trendable inflammation
    // marker — same "diagnostic serology → other" rule as the infectious panel.
    category: "other",
    panel: "autoimmune",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Rheumatoid Factor", "RF"],
  },
  "53027-9": {
    loincCode: "53027-9",
    displayName: "Anti-CCP Antibody",
    detailDisplayName: "Anti-Cyclic Citrullinated Peptide Antibody",
    shortName: "Anti-CCP",
    unit: "U/mL",
    // Autoimmune autoantibodies are disease diagnostics (lupus/RA), not a trendable inflammation
    // marker — same "diagnostic serology → other" rule as the infectious panel.
    category: "other",
    panel: "autoimmune",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Anti-CCP", "Cyclic Citrullinated Peptide"],
  },
  "11013-0": {
    loincCode: "11013-0",
    displayName: "Anti-dsDNA Antibody",
    shortName: "Anti-dsDNA",
    unit: "titer",
    // Autoimmune autoantibodies are disease diagnostics (lupus/RA), not a trendable inflammation
    // marker — same "diagnostic serology → other" rule as the infectious panel.
    category: "other",
    panel: "autoimmune",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Anti-dsDNA", "dsDNA", "DNA double strand Ab"],
  },
  "56888-1": {
    loincCode: "56888-1",
    displayName: "HIV Ag/Ab",
    detailDisplayName: "HIV Antibody/Antigen",
    shortName: "HIV",
    unit: "",
    // Infectious-disease serology, not a functional system — qualitative diagnostic, not a trend.
    category: "other",
    panel: "infectious",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["HIV Ag/Ab", "HIV 1+2 Ab", "HIV p24"],
  },
  "5196-1": {
    loincCode: "5196-1",
    displayName: "Hepatitis B Surface Antigen",
    shortName: "HBsAg",
    unit: "",
    // Infectious-disease serology, not a functional system — qualitative diagnostic, not a trend.
    category: "other",
    panel: "infectious",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["HBsAg", "Hepatitis B Surface Antigen"],
  },
  "94500-6": {
    loincCode: "94500-6",
    displayName: "COVID-19 PCR",
    shortName: "COVID-19",
    unit: "",
    // Infectious-disease serology, not a functional system — qualitative diagnostic, not a trend.
    category: "other",
    panel: "infectious",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["COVID-19", "SARS-CoV-2", "COVID PCR"],
  },

  // ---------------------------------------------------------------------------
  // Lab Results - Electrolytes & Kidney (Metabolism)
  // ---------------------------------------------------------------------------
  "2160-0": {
    loincCode: "2160-0",
    displayName: "Creatinine",
    shortName: "Cr",
    unit: "mg/dL",
    // Kidney-function markers have no functional-system home — there's no `renal` category among the
    // nine — so they spill to `other` for now rather than being forced into `metabolism`. Trade-off:
    // they're hidden from the picker until a `renal` system exists. `panel` still clusters them in
    // By-Panel sort. Applies to the kidney group: Creatinine, BUN, BUN/Cr, eGFR, Cystatin C, Urine
    // Protein, Urine ACR.
    category: "other",
    panel: "bmp",
    modelClass: null,
    referenceRange: LAB_RANGES.creatinine,
    supportsCQ: false,
    isLabResult: true,
    // Legacy imports often store serum/plasma creatinine under this sibling code.
    equivalentLoincCodes: ["2161-8"],
    textSearchTerms: ["Creatinine"],
  },
  "3094-0": {
    loincCode: "3094-0",
    displayName: "Blood Urea Nitrogen",
    shortName: "BUN",
    unit: "mg/dL",
    category: "other",
    panel: "bmp",
    modelClass: null,
    referenceRange: LAB_RANGES.bun,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["14937-7", "12964-3"],
    textSearchTerms: ["BUN", "Urea Nitrogen"],
  },
  "3097-3": {
    loincCode: "3097-3",
    displayName: "BUN/Creatinine Ratio",
    shortName: "BUN/Cr",
    unit: "ratio",
    category: "other",
    panel: "bmp",
    modelClass: null,
    referenceRange: LAB_RANGES.bunCreatinineRatio,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["BUN/Creatinine Ratio", "BUN/Creat Ratio", "Urea/Creatinine Ratio"],
  },
  "62238-1": {
    loincCode: "62238-1",
    displayName: "eGFR",
    shortName: "eGFR",
    unit: "mL/min/1.73m2",
    category: "other",
    panel: "bmp",
    modelClass: null,
    referenceRange: LAB_RANGES.egfr,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["33914-3", "98979-8"],
    textSearchTerms: ["eGFR", "Glomerular Filtration Rate"],
  },
  "2823-3": {
    loincCode: "2823-3",
    displayName: "Potassium",
    shortName: "K",
    unit: "mmol/L",
    category: "metabolism",
    panel: "bmp",
    modelClass: null,
    referenceRange: LAB_RANGES.potassium,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Potassium"],
  },
  "2951-2": {
    loincCode: "2951-2",
    displayName: "Sodium",
    shortName: "Na",
    unit: "mmol/L",
    category: "metabolism",
    panel: "bmp",
    modelClass: null,
    referenceRange: LAB_RANGES.sodium,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Sodium"],
  },
  "2075-0": {
    loincCode: "2075-0",
    displayName: "Chloride",
    shortName: "Cl",
    unit: "mmol/L",
    category: "metabolism",
    panel: "bmp",
    modelClass: null,
    referenceRange: LAB_RANGES.chloride,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Chloride"],
  },
  "2028-9": {
    loincCode: "2028-9",
    displayName: "Carbon Dioxide",
    shortName: "CO2",
    unit: "mmol/L",
    category: "metabolism",
    panel: "bmp",
    modelClass: null,
    referenceRange: LAB_RANGES.carbonDioxide,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Carbon Dioxide"],
  },
  "1963-8": {
    loincCode: "1963-8",
    displayName: "Bicarbonate",
    shortName: "HCO3",
    unit: "mmol/L",
    category: "metabolism",
    panel: "bmp",
    modelClass: null,
    referenceRange: LAB_RANGES.bicarbonate,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["1959-6"],
    textSearchTerms: ["Bicarbonate"],
  },
  "33037-3": {
    loincCode: "33037-3",
    displayName: "Anion Gap",
    shortName: "Anion Gap",
    unit: "mmol/L",
    category: "metabolism",
    // Derived from BMP electrolytes and reported on every BMP/CMP.
    panel: "bmp",
    modelClass: null,
    referenceRange: LAB_RANGES.anionGap,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["1863-0", "10466-1"],
    textSearchTerms: ["Anion Gap"],
  },
  "32294-1": {
    loincCode: "32294-1",
    displayName: "Urine Albumin/Creatinine Ratio",
    shortName: "Urine ACR",
    unit: "mg/g",
    category: "other",
    panel: "renal",
    modelClass: null,
    referenceRange: LAB_RANGES.urineAlbuminCreatinineRatio,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Albumin/Creatinine", "Microalbumin/Creatinine", "Urine ACR"],
  },
  "2888-6": {
    loincCode: "2888-6",
    displayName: "Urine Protein",
    shortName: "Urine Protein",
    unit: "mg/dL",
    category: "other",
    panel: "renal",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["2887-8"],
    textSearchTerms: ["Urine Protein", "Protein, Urine"],
  },
  "33863-2": {
    loincCode: "33863-2",
    displayName: "Cystatin C",
    shortName: "Cystatin C",
    unit: "mg/L",
    category: "other",
    panel: "renal",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Cystatin C"],
  },
  "3084-1": {
    loincCode: "3084-1",
    displayName: "Uric Acid",
    shortName: "UA",
    unit: "mg/dL",
    category: "metabolism",
    panel: "inflammation",
    modelClass: null,
    referenceRange: LAB_RANGES.uricAcid,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Uric Acid"],
  },
  "2484-4": {
    loincCode: "2484-4",
    displayName: "Insulin",
    shortName: "Ins",
    unit: "µIU/mL",
    category: "metabolism",
    panel: "glycemic",
    modelClass: null,
    referenceRange: LAB_RANGES.insulin,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Insulin"],
  },

  // ---------------------------------------------------------------------------
  // Lab Results - Substance Levels (Other)
  // ---------------------------------------------------------------------------
  // Environmental heavy metals (Lead/Mercury/Arsenic) and drug levels
  // (Acetaminophen/Lithium) have no home among the nine functional systems — they're
  // toxicology/exposure/therapeutic-drug-monitoring, not a metabolic/cardio/endocrine
  // concept. They previously defaulted to `metabolism` for lack of a better slot, which
  // surfaced an acetaminophen level next to glucose and HbA1c under "Metabolism". Category
  // is `other` (hidden from the picker, per FunctionalSystem docs); `panel: "toxicology"`
  // still groups them clinically in the By-Panel sort.
  "5671-3": {
    loincCode: "5671-3",
    displayName: "Lead",
    shortName: "Lead",
    unit: "ug/dL",
    category: "other",
    panel: "toxicology",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["10368-9", "77307-7"],
    textSearchTerms: ["Lead"],
  },
  "5685-3": {
    loincCode: "5685-3",
    displayName: "Mercury",
    shortName: "Mercury",
    unit: "ug/L",
    category: "other",
    panel: "toxicology",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["25170-2"],
    textSearchTerms: ["Mercury"],
  },
  "5583-0": {
    loincCode: "5583-0",
    displayName: "Arsenic",
    shortName: "Arsenic",
    unit: "ug/L",
    category: "other",
    panel: "toxicology",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Arsenic"],
  },
  "3298-7": {
    loincCode: "3298-7",
    displayName: "Acetaminophen",
    shortName: "APAP",
    unit: "ug/mL",
    category: "other",
    panel: "toxicology",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Acetaminophen", "APAP", "Tylenol"],
  },
  "14334-7": {
    loincCode: "14334-7",
    displayName: "Lithium",
    shortName: "Lithium",
    unit: "mmol/L",
    category: "other",
    panel: "toxicology",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Lithium"],
  },

  // ---------------------------------------------------------------------------
  // Lab Results - Nutrients
  // ---------------------------------------------------------------------------
  "1751-7": {
    loincCode: "1751-7",
    displayName: "Albumin",
    shortName: "Alb",
    unit: "g/dL",
    category: "nutrients",
    panel: "liver",
    modelClass: null,
    referenceRange: LAB_RANGES.albumin,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Albumin"],
  },
  "17861-6": {
    loincCode: "17861-6",
    displayName: "Calcium",
    shortName: "Ca",
    unit: "mg/dL",
    category: "nutrients",
    panel: "bmp",
    modelClass: null,
    referenceRange: LAB_RANGES.calcium,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Calcium"],
  },
  "2284-8": {
    loincCode: "2284-8",
    displayName: "Folate",
    shortName: "Folate",
    unit: "ng/mL",
    category: "nutrients",
    panel: "vitamins",
    modelClass: null,
    referenceRange: LAB_RANGES.folate,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["14732-2"],
    textSearchTerms: ["Folate"],
  },
  "2498-4": {
    loincCode: "2498-4",
    displayName: "Iron",
    shortName: "Iron",
    unit: "ug/dL",
    category: "nutrients",
    panel: "iron",
    modelClass: null,
    referenceRange: LAB_RANGES.iron,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["14798-3"],
    textSearchTerms: ["Iron"],
  },
  "2276-4": {
    loincCode: "2276-4",
    displayName: "Ferritin",
    shortName: "Ferritin",
    unit: "ng/mL",
    category: "nutrients",
    panel: "iron",
    modelClass: null,
    referenceRange: LAB_RANGES.ferritin,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["14723-1", "20567-4"],
    textSearchTerms: ["Ferritin"],
  },
  "3034-6": {
    loincCode: "3034-6",
    displayName: "Transferrin",
    shortName: "Transferrin",
    unit: "mg/dL",
    category: "nutrients",
    panel: "iron",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Transferrin"],
  },
  "2500-7": {
    loincCode: "2500-7",
    displayName: "Total Iron-Binding Capacity",
    shortName: "TIBC",
    unit: "ug/dL",
    category: "nutrients",
    panel: "iron",
    modelClass: null,
    referenceRange: null,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["TIBC", "Iron Binding Capacity", "Total Iron-Binding Capacity"],
  },
  "2502-3": {
    loincCode: "2502-3",
    displayName: "Transferrin Saturation",
    shortName: "TSAT",
    unit: "%",
    category: "nutrients",
    panel: "iron",
    modelClass: null,
    referenceRange: LAB_RANGES.transferrinSaturation,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Transferrin Saturation", "Iron Saturation", "% Saturation", "TSAT"],
  },
  "2132-9": {
    loincCode: "2132-9",
    displayName: "Vitamin B12",
    shortName: "B12",
    unit: "pg/mL",
    category: "nutrients",
    panel: "vitamins",
    modelClass: null,
    referenceRange: LAB_RANGES.vitaminB12,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["1888-7"],
    textSearchTerms: ["Vitamin B12", "Cobalamin"],
  },
  "5763-8": {
    loincCode: "5763-8",
    displayName: "Zinc",
    shortName: "Zinc",
    unit: "ug/dL",
    category: "nutrients",
    panel: "vitamins",
    modelClass: null,
    referenceRange: LAB_RANGES.zinc,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["14955-9"],
    textSearchTerms: ["Zinc"],
  },
  "1823-4": {
    loincCode: "1823-4",
    displayName: "Vitamin E (Alpha Tocopherol)",
    shortName: "Vitamin E",
    unit: "mg/L",
    category: "nutrients",
    panel: "vitamins",
    modelClass: null,
    referenceRange: LAB_RANGES.alphaTocopherol,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["14590-4"],
    textSearchTerms: ["Vitamin E", "Alpha Tocopherol"],
  },
  "19123-9": {
    loincCode: "19123-9",
    displayName: "Magnesium",
    shortName: "Mg",
    unit: "mg/dL",
    category: "nutrients",
    panel: "vitamins",
    modelClass: null,
    referenceRange: LAB_RANGES.magnesium,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["2601-3"],
    textSearchTerms: ["Magnesium"],
  },

  // ---------------------------------------------------------------------------
  // Lab Results - Musculoskeletal (Bone Health)
  // ---------------------------------------------------------------------------
  "6768-6": {
    loincCode: "6768-6",
    displayName: "Alkaline Phosphatase",
    shortName: "ALP",
    unit: "U/L",
    category: "muscular_skeletal",
    panel: "liver",
    modelClass: null,
    referenceRange: LAB_RANGES.alkalinePhosphatase,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Alkaline Phosphatase"],
  },
  "2777-1": {
    loincCode: "2777-1",
    displayName: "Phosphorus",
    shortName: "Phos",
    unit: "mg/dL",
    category: "muscular_skeletal",
    panel: "vitamins",
    modelClass: null,
    referenceRange: LAB_RANGES.phosphorus,
    supportsCQ: false,
    isLabResult: true,
  },
  "2731-8": {
    loincCode: "2731-8",
    displayName: "Parathyroid Hormone",
    shortName: "PTH",
    unit: "pg/mL",
    category: "muscular_skeletal",
    panel: "bone",
    modelClass: null,
    referenceRange: LAB_RANGES.pth,
    supportsCQ: false,
    isLabResult: true,
  },
  "2695-5": {
    loincCode: "2695-5",
    displayName: "Osteocalcin",
    shortName: "OC",
    unit: "ng/mL",
    category: "muscular_skeletal",
    panel: "bone",
    modelClass: null,
    referenceRange: LAB_RANGES.osteocalcin,
    supportsCQ: false,
    isLabResult: true,
  },
  "82523-2": {
    loincCode: "82523-2",
    displayName: "CTX (C-telopeptide)",
    shortName: "CTX",
    unit: "ng/mL",
    category: "muscular_skeletal",
    panel: "bone",
    modelClass: null,
    referenceRange: LAB_RANGES.ctx,
    supportsCQ: false,
    isLabResult: true,
  },
  "47255-5": {
    loincCode: "47255-5",
    displayName: "P1NP (Procollagen Type 1)",
    shortName: "P1NP",
    unit: "ng/mL",
    category: "muscular_skeletal",
    panel: "bone",
    modelClass: null,
    referenceRange: LAB_RANGES.p1np,
    supportsCQ: false,
    isLabResult: true,
    equivalentLoincCodes: ["77370-5"],
    textSearchTerms: ["P1NP", "PINP", "Procollagen"],
  },

  // ---------------------------------------------------------------------------
  // Lab Results - Musculoskeletal (Muscle Health)
  // ---------------------------------------------------------------------------
  "2157-6": {
    loincCode: "2157-6",
    displayName: "Creatine Kinase",
    shortName: "CK",
    unit: "U/L",
    category: "muscular_skeletal",
    panel: "muscle",
    modelClass: null,
    referenceRange: LAB_RANGES.creatineKinase,
    supportsCQ: false,
    isLabResult: true,
    textSearchTerms: ["Creatine Kinase"],
  },
  "2639-3": {
    loincCode: "2639-3",
    displayName: "Myoglobin",
    shortName: "Myo",
    unit: "ng/mL",
    category: "muscular_skeletal",
    panel: "muscle",
    modelClass: null,
    referenceRange: LAB_RANGES.myoglobin,
    supportsCQ: false,
    isLabResult: true,
  },
  "2532-0": {
    loincCode: "2532-0",
    displayName: "Lactate Dehydrogenase",
    shortName: "LDH",
    unit: "U/L",
    category: "muscular_skeletal",
    panel: "muscle",
    modelClass: null,
    referenceRange: LAB_RANGES.ldh,
    supportsCQ: false,
    isLabResult: true,
  },
  "1764-0": {
    loincCode: "1764-0",
    displayName: "Aldolase",
    shortName: "Aldo",
    unit: "U/L",
    category: "muscular_skeletal",
    panel: "muscle",
    modelClass: null,
    referenceRange: LAB_RANGES.aldolase,
    supportsCQ: false,
    isLabResult: true,
  },

  // ===========================================================================
  // Pre-registered Continuous Wearable Metrics
  // ===========================================================================
  // These entries are placeholders for metrics from the Continuous Wearables
  // roadmap. They have `supportsCQ: true` so they render as hero cards and
  // `modelClass: null` because data pipelines don't exist yet. When data starts
  // flowing (observations with matching codes), these will automatically appear
  // in the Trends hero card UI with the correct chart type.
  //
  // Metrics using real LOINC codes reference VitalLOINCCodes.
  // Computed / device-specific metrics use `vibrant.*` custom codes that should
  // match the codes produced by future data pipelines.
  // ===========================================================================

  // ---------------------------------------------------------------------------
  // Cardiovascular — Computed / API Metrics
  // ---------------------------------------------------------------------------
  "vibrant.bp-variability": {
    loincCode: "vibrant.bp-variability",
    displayName: "BP Variability",
    shortName: "BPV",
    unit: "mmHg",
    category: "cardiovascular",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
  },
  "vibrant.pulse-pressure": {
    loincCode: "vibrant.pulse-pressure",
    displayName: "Pulse Pressure",
    shortName: "PP",
    unit: "mmHg",
    category: "cardiovascular",
    modelClass: null,
    referenceRange: WEARABLE_RANGES.pulsePressure,
    supportsCQ: true,
  },
  "vibrant.resting-hr-deviation": {
    loincCode: "vibrant.resting-hr-deviation",
    displayName: "Resting HR Deviation",
    shortName: "RHR Δ",
    unit: "bpm",
    category: "cardiovascular",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
  },
  "vibrant.nighttime-hr": {
    loincCode: "vibrant.nighttime-hr",
    displayName: "Nighttime Heart Rate",
    shortName: "Night HR",
    unit: "bpm",
    category: "cardiovascular",
    modelClass: null,
    referenceRange: WEARABLE_RANGES.nighttimeHR,
    supportsCQ: true,
  },
  "vibrant.cardio-fitness-score": {
    loincCode: "vibrant.cardio-fitness-score",
    displayName: "Cardio Fitness Score",
    shortName: "Cardio",
    unit: "",
    category: "cardiovascular",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
  },

  // ---------------------------------------------------------------------------
  // Metabolism — CGM Computed Metrics
  // ---------------------------------------------------------------------------
  "vibrant.cgm-time-in-range": {
    loincCode: "vibrant.cgm-time-in-range",
    displayName: "CGM Time in Range",
    shortName: "TIR",
    unit: "%",
    category: "metabolism",
    modelClass: null,
    referenceRange: WEARABLE_RANGES.cgmTimeInRange,
    supportsCQ: true,
    chartType: "circle",
  },
  "vibrant.cgm-variability": {
    loincCode: "vibrant.cgm-variability",
    displayName: "CGM Variability (CV%)",
    shortName: "CGM CV",
    unit: "%",
    category: "metabolism",
    modelClass: null,
    referenceRange: WEARABLE_RANGES.cgmVariability,
    supportsCQ: true,
  },

  // ---------------------------------------------------------------------------
  // Metabolism — Activity (API / Wearable)
  // ---------------------------------------------------------------------------
  "vibrant.activity-load": {
    loincCode: "vibrant.activity-load",
    displayName: "Activity Load (Strain)",
    shortName: "Strain",
    unit: "",
    category: "metabolism",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
  },
  "vibrant.step-cadence": {
    loincCode: "vibrant.step-cadence",
    displayName: "Step Cadence",
    shortName: "Cadence",
    unit: "steps/min",
    category: "metabolism",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
  },
  "vibrant.active-minutes": {
    loincCode: "vibrant.active-minutes",
    displayName: "Active Minutes",
    shortName: "Active",
    unit: "min",
    category: "metabolism",
    modelClass: null,
    referenceRange: WEARABLE_RANGES.activeMinutes,
    supportsCQ: true,
    chartType: "bar",
  },

  // ---------------------------------------------------------------------------
  // Autonomic — Wearable / API Metrics
  // ---------------------------------------------------------------------------
  "vibrant.skin-temp-deviation": {
    loincCode: "vibrant.skin-temp-deviation",
    displayName: "Skin Temperature Deviation",
    shortName: "Temp Δ",
    unit: "°C",
    category: "autonomic",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
  },
  "vibrant.recovery-score": {
    loincCode: "vibrant.recovery-score",
    displayName: "Recovery Score",
    shortName: "Recovery",
    unit: "",
    category: "autonomic",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
  },
  "vibrant.stress-load-score": {
    loincCode: "vibrant.stress-load-score",
    displayName: "Stress Load Score",
    shortName: "Stress",
    unit: "",
    category: "autonomic",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
  },
  "vibrant.sns-pns-balance": {
    loincCode: "vibrant.sns-pns-balance",
    displayName: "Sympathetic vs Parasympathetic Balance",
    shortName: "SNS/PNS",
    unit: "%",
    category: "autonomic",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
    chartType: "circle",
  },
  "vibrant.recovery-strain-balance": {
    loincCode: "vibrant.recovery-strain-balance",
    displayName: "Recovery vs Strain Balance",
    shortName: "Rec/Strain",
    unit: "%",
    category: "autonomic",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
    chartType: "circle",
  },

  // ---------------------------------------------------------------------------
  // Musculoskeletal — Fitness Metrics
  // ---------------------------------------------------------------------------
  "vibrant.grip-strength": {
    loincCode: "vibrant.grip-strength",
    displayName: "Grip Strength",
    shortName: "Grip",
    unit: "kg",
    category: "muscular_skeletal",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
  },
  "vibrant.mobility-assessments": {
    loincCode: "vibrant.mobility-assessments",
    displayName: "Mobility Assessments",
    shortName: "Mobility",
    unit: "",
    category: "muscular_skeletal",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
  },
  "vibrant.training-load": {
    loincCode: "vibrant.training-load",
    displayName: "Training Load",
    shortName: "Load",
    unit: "",
    category: "muscular_skeletal",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
  },

  // ---------------------------------------------------------------------------
  // Sleep — API / Wearable Metrics
  // ---------------------------------------------------------------------------
  "vibrant.total-sleep-time": {
    loincCode: "vibrant.total-sleep-time",
    displayName: "Total Sleep Time",
    shortName: "TST",
    unit: "min",
    category: "sleep",
    modelClass: null,
    referenceRange: WEARABLE_RANGES.sleepDuration,
    supportsCQ: true,
  },
  "vibrant.waso": {
    loincCode: "vibrant.waso",
    displayName: "Wake After Sleep Onset",
    shortName: "WASO",
    unit: "min",
    category: "sleep",
    modelClass: null,
    referenceRange: WEARABLE_RANGES.waso,
    supportsCQ: true,
  },
  "vibrant.sleep-stages": {
    loincCode: "vibrant.sleep-stages",
    displayName: "Sleep Stages (Deep / REM)",
    shortName: "Stages",
    unit: "%",
    category: "sleep",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
    chartType: "circle",
  },
  "vibrant.sleep-regularity": {
    loincCode: "vibrant.sleep-regularity",
    displayName: "Sleep Regularity",
    shortName: "Regularity",
    unit: "%",
    category: "sleep",
    modelClass: null,
    referenceRange: WEARABLE_RANGES.sleepRegularity,
    supportsCQ: true,
  },
  "vibrant.night-awakenings": {
    loincCode: "vibrant.night-awakenings",
    displayName: "Night Awakenings",
    shortName: "Wakes",
    unit: "",
    category: "sleep",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
    chartType: "bar",
  },
  "vibrant.sleep-consistency-score": {
    loincCode: "vibrant.sleep-consistency-score",
    displayName: "Sleep Consistency Score",
    shortName: "Consistency",
    unit: "",
    category: "sleep",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
  },
  "vibrant.sleep-latency": {
    loincCode: "vibrant.sleep-latency",
    displayName: "Sleep Latency",
    shortName: "Latency",
    unit: "min",
    category: "sleep",
    modelClass: null,
    referenceRange: WEARABLE_RANGES.sleepLatency,
    supportsCQ: true,
  },
  "vibrant.sleep-hrv": {
    loincCode: "vibrant.sleep-hrv",
    displayName: "Sleep HRV",
    shortName: "Sleep HRV",
    unit: "ms",
    category: "sleep",
    modelClass: null,
    referenceRange: WEARABLE_RANGES.hrv,
    supportsCQ: true,
  },

  // ---------------------------------------------------------------------------
  // Readiness — Device-specific (Oura)
  // ---------------------------------------------------------------------------
  "vibrant.readiness-score": {
    loincCode: "vibrant.readiness-score",
    displayName: "Readiness Score",
    shortName: "Ready",
    unit: "",
    category: "autonomic",
    modelClass: null,
    referenceRange: null,
    supportsCQ: true,
  },
} as const;

// -----------------------------------------------------------------------------
// Helper Functions
// -----------------------------------------------------------------------------

/**
 * Get visible functional systems (excludes the hidden "other" category), ordered
 * alphabetically by display label. This is the canonical render order for UI category
 * pickers and "By category" section headers, so labs and devices group in the same
 * A–Z order the web surfaces use.
 *
 * @returns Array of visible FunctionalSystem values, sorted by display name
 */
export function getVisibleFunctionalSystems(): FunctionalSystem[] {
  return (Object.keys(FUNCTIONAL_SYSTEMS) as FunctionalSystem[])
    .filter((system) => system !== "other")
    .sort((a, b) => FUNCTIONAL_SYSTEMS[a].name.localeCompare(FUNCTIONAL_SYSTEMS[b].name));
}

/**
 * Get biomarker config by LOINC code.
 * @param loincCode - The LOINC code to look up
 * @returns BiomarkerConfig or undefined if not found
 */
export function getBiomarkerConfig(loincCode: string): BiomarkerConfig | undefined {
  return BIOMARKER_CONFIG[loincCode];
}

/**
 * Get all biomarkers for a functional system.
 * @param system - The functional system to filter by
 * @returns Array of BiomarkerConfig
 */
export function getBiomarkersBySystem(system: FunctionalSystem): BiomarkerConfig[] {
  return Object.values(BIOMARKER_CONFIG).filter((config) => config.category === system);
}

/**
 * Get all lab result biomarkers.
 * @returns Array of BiomarkerConfig for lab results
 */
export function getLabBiomarkers(): BiomarkerConfig[] {
  return Object.values(BIOMARKER_CONFIG).filter((config) => config.isLabResult === true);
}

/**
 * Get all wearable biomarkers.
 * @returns Array of BiomarkerConfig for wearables
 */
export function getWearableBiomarkers(): BiomarkerConfig[] {
  return Object.values(BIOMARKER_CONFIG).filter((config) => config.isLabResult !== true);
}

/**
 * Get biomarkers that support Continuous Query caching.
 * @returns Array of BiomarkerConfig that support CQ
 */
export function getCQSupportedBiomarkers(): BiomarkerConfig[] {
  return Object.values(BIOMARKER_CONFIG).filter((config) => config.supportsCQ);
}

/**
 * Get all LOINC codes for a functional category.
 * Uses BIOMARKER_CONFIG as source of truth (includes both wearables and labs).
 * @param category - The functional category
 * @returns Array of LOINC codes for that category
 */
export function getLoincCodesByCategory(category: FunctionalSystem): string[] {
  return getBiomarkersBySystem(category).map((config) => config.loincCode);
}
